import {
  DataGridBox,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import {
  UpdateEffortStatusContactDialog,
  CreateEffortStatusContactDialog,
  DeleteEffortStatusContactDialog,
} from './components';
import apiService from 'library/api-service';
import { Grid2 as Grid } from '@mui/material';
import { EFFORT_STATUS_TYPE_ENUM } from 'enums';
import { renderDataGridLocaleText } from 'utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLookup, useSingleRowSelectionGuard } from 'hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EffortContactItem, EffortStatusContactItem } from 'library/types';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';

const EffortStatusContacts = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();
  const { items: effortContacts } = useLookup<EffortContactItem>(apiService.getEffortContacts);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [openDeleteEffortStatusContact, setOpenDeleteEffortStatusContact] = useState(false);
  const [openCreateEffortStatusContact, setOpenCreateEffortStatusContact] = useState(false);
  const [openUpdateEffortStatusContact, setOpenUpdateEffortStatusContact] = useState(false);
  const [effortStatusContacts, setEffortStatusContacts] = useState<EffortStatusContactItem[]>([]);

  // translate functions
  const getEffortStatusTypeTranslated = useCallback(
    (effortStatusType: number) =>
      ({
        [EFFORT_STATUS_TYPE_ENUM.IN_SALES]: intl.formatMessage({
          id: 'enum.effortStatusType.inSales',
          defaultMessage: 'Satış Ekibinde',
        }),
        [EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL]: intl.formatMessage({
          id: 'enum.effortStatusType.pendingCustomerApproval',
          defaultMessage: 'Müşteri Onayında',
        }),
        [EFFORT_STATUS_TYPE_ENUM.APPROVED]: intl.formatMessage({
          id: 'enum.effortStatusType.approved',
          defaultMessage: 'Efor Onaylandı',
        }),
        [EFFORT_STATUS_TYPE_ENUM.REJECTED]: intl.formatMessage({
          id: 'enum.effortStatusType.rejected',
          defaultMessage: 'Efor Reddedildi',
        }),
        [EFFORT_STATUS_TYPE_ENUM.WORK_STARTED]: intl.formatMessage({
          id: 'enum.effortStatusType.workStarted',
          defaultMessage: 'Çalışma Başlatıldı',
        }),
        [EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER]: intl.formatMessage({
          id: 'enum.effortStatusType.testingByCustomer',
          defaultMessage: 'Müşteri Testinde',
        }),
        [EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED]: intl.formatMessage({
          id: 'enum.effortStatusType.workCompleted',
          defaultMessage: 'Çalışma Tamamlandı',
        }),
        [EFFORT_STATUS_TYPE_ENUM.INVOICED]: intl.formatMessage({
          id: 'enum.effortStatusType.invoiced',
          defaultMessage: 'Faturası Kesildi',
        }),
      })[effortStatusType],
    [intl]
  );

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: intl.formatMessage({
          id: 'effortTracing.status',
          defaultMessage: 'Statü',
        }),
        headerAlign: 'center',
        align: 'center',
        minWidth: 250,
        valueGetter: (value) => getEffortStatusTypeTranslated(value),
      },
      {
        field: 'toContacts',
        headerName: 'TO',
        flex: 1,
        valueGetter: (value: number[]) =>
          value.map((v) => effortContacts.find((effortContact) => effortContact.id === v)?.title).join(', '),
      },
      {
        field: 'ccContacts',
        headerName: 'CC',
        flex: 1,
        valueGetter: (value: number[]) =>
          value.map((v) => effortContacts.find((effortContact) => effortContact.id === v)?.title).join(', '),
      },
    ],
    [intl, effortContacts, getEffortStatusTypeTranslated]
  );

  // sync helper functions
  const handleCloseDeleteEffortStatusContact = () => setOpenDeleteEffortStatusContact(false);
  const handleClickOpenDeleteEffortStatusContact = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenDeleteEffortStatusContact(true);
  };

  const handleCloseCreateEffortStatusContact = () => setOpenCreateEffortStatusContact(false);
  const handleClickOpenCreateEffortStatusContact = () => setOpenCreateEffortStatusContact(true);

  const handleCloseUpdateEffortStatusContact = () => setOpenUpdateEffortStatusContact(false);
  const handleClickOpenUpdateEffortStatusContact = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenUpdateEffortStatusContact(true);
  };

  // async helper functions
  const getEffortStatusContacts = async () => {
    try {
      setLoading(true);

      const response = await apiService.getEffortStatusContacts();

      if (response.isSuccess) {
        const duplicateArr = [...response.data];
        const sortedData = duplicateArr.sort((a, b) => a.status - b.status);

        setEffortStatusContacts(sortedData);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // useEffect hooks
  useEffect(() => {
    if (effortContacts.length) {
      getEffortStatusContacts();
    }
  }, [effortContacts]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-effort-status-contact']}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateEffortStatusContact}>
              <FormattedMessage
                id="effortStatusContacts.createEffortStatusContact"
                defaultMessage="Efor Statü Muhatabı Oluştur"
              />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-effort-status-contact']}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateEffortStatusContact}>
              <FormattedMessage
                id="effortStatusContacts.updateEffortStatusContact"
                defaultMessage="Efor Statü Muhatabı Düzenle"
              />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-effort-status-contact']}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteEffortStatusContact}>
              <FormattedMessage
                id="effortStatusContacts.deleteEffortStatusContact"
                defaultMessage="Efor Statü Muhatabı Sil"
              />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateEffortStatusContactDialog
        open={openCreateEffortStatusContact}
        handleClose={handleCloseCreateEffortStatusContact}
        refresh={getEffortStatusContacts}
        effortContacts={effortContacts}
        effortStatusContacts={effortStatusContacts}
        translateFunction={getEffortStatusTypeTranslated}
      />
      <UpdateEffortStatusContactDialog
        open={openUpdateEffortStatusContact}
        effortStatusContactId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateEffortStatusContact}
        refresh={getEffortStatusContacts}
        effortContacts={effortContacts}
        translateFunction={getEffortStatusTypeTranslated}
      />
      <DeleteEffortStatusContactDialog
        open={openDeleteEffortStatusContact}
        effortStatusContactId={rowSelectionModel[0]}
        handleClose={handleCloseDeleteEffortStatusContact}
        refresh={getEffortStatusContacts}
        setRowSelectionModel={setRowSelectionModel}
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={effortStatusContacts}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default EffortStatusContacts;
